import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  WalletCards,
  ArrowDownCircle,
  TrendingUp,
  Receipt,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  CalendarDays,
  X,
  Save
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

function dinheiro(valor) {
  const numero =
    Number(valor || 0);

  return numero.toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  );
}

function dataBr(valor) {
  if (!valor) {
    return '-';
  }

  const texto =
    String(valor);

  const match =
    texto.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (match) {
    return (
      match[3] +
      '/' +
      match[2] +
      '/' +
      match[1]
    );
  }

  const data =
    new Date(valor);

  if (
    Number.isNaN(
      data.getTime()
    )
  ) {
    return '-';
  }

  return data.toLocaleDateString(
    'pt-BR'
  );
}

function hojeInput() {
  const agora =
    new Date();

  const pad =
    valor =>
      String(valor)
        .padStart(2, '0');

  return (
    agora.getFullYear() +
    '-' +
    pad(
      agora.getMonth() + 1
    ) +
    '-' +
    pad(
      agora.getDate()
    )
  );
}

async function apiRequest(
  caminho,
  options = {}
) {
  const config = {
    credentials: 'include',
    ...options
  };

  if (
    config.body &&
    !config.headers
  ) {
    config.headers = {
      'Content-Type':
        'application/json'
    };
  }

  const response =
    await fetch(
      `${API}${caminho}`,
      config
    );

  let data = {};

  try {
    data =
      await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok) {
    throw new Error(
      data.error ||
      'Não foi possível concluir a operação.'
    );
  }

  return data;
}

export default function Financeiro() {
  const {
    darkMode
  } = useSettings();

  const agora =
    new Date();

  const [
    year,
    setYear
  ] =
    useState(
      agora.getFullYear()
    );

  const [
    month,
    setMonth
  ] =
    useState(
      agora.getMonth() + 1
    );

  const [
    summary,
    setSummary
  ] =
    useState(null);

  const [
    report,
    setReport
  ] =
    useState({
      entries: [],
      expenses: []
    });

  const [
    annual,
    setAnnual
  ] =
    useState({
      months: [],
      totals: null
    });

  const [
    perfil,
    setPerfil
  ] =
    useState(null);

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    saving,
    setSaving
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState('');

  const [
    success,
    setSuccess
  ] =
    useState('');

  const [
    formOpen,
    setFormOpen
  ] =
    useState(false);

  const [
    editingId,
    setEditingId
  ] =
    useState(null);

  const [
    form,
    setForm
  ] =
    useState({
      expense_date:
        hojeInput(),
      amount: '',
      category: '',
      description: '',
      notes: ''
    });

  const isMaster =
    perfil === 'master';

  const anos =
    useMemo(
      () => {
        const atual =
          new Date().getFullYear();

        return Array.from(
          {
            length: 8
          },
          (_, index) =>
            atual + 1 - index
        );
      },
      []
    );

  const cardClass =
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900';

  const subtleClass =
    darkMode
      ? 'text-gray-400'
      : 'text-gray-500';

  const inputClass =
    darkMode
      ? 'bg-gray-900 border-gray-700 text-white'
      : 'bg-white border-gray-300 text-gray-900';

  const carregarPerfil =
    useCallback(
      async () => {
        try {
          const data =
            await apiRequest(
              '/auth/me'
            );

          setPerfil(
            data.perfil || null
          );
        } catch (erro) {
          setPerfil(null);
        }
      },
      []
    );

  const carregar =
    useCallback(
      async () => {
        setLoading(true);
        setError('');

        try {
          const query =
            `?year=${year}&month=${month}`;

          const [
            summaryData,
            reportData,
            annualData
          ] =
            await Promise.all([
              apiRequest(
                `/financial/summary${query}`
              ),
              apiRequest(
                `/financial/report${query}`
              ),
              apiRequest(
                `/financial/annual?year=${year}`
              )
            ]);

          setSummary(
            summaryData
          );

          setReport({
            entries:
              Array.isArray(
                reportData.entries
              )
                ? reportData.entries
                : [],

            expenses:
              Array.isArray(
                reportData.expenses
              )
                ? reportData.expenses
                : []
          });

          setAnnual({
            months:
              Array.isArray(
                annualData.months
              )
                ? annualData.months
                : [],

            totals:
              annualData.totals ||
              null
          });
        } catch (erro) {
          setError(
            erro.message
          );
        } finally {
          setLoading(false);
        }
      },
      [
        year,
        month
      ]
    );

  useEffect(
    () => {
      carregarPerfil();
    },
    [
      carregarPerfil
    ]
  );

  useEffect(
    () => {
      carregar();
    },
    [
      carregar
    ]
  );

  function limparForm() {
    setEditingId(null);

    setForm({
      expense_date:
        hojeInput(),
      amount: '',
      category: '',
      description: '',
      notes: ''
    });
  }

  function abrirNovaDespesa() {
    limparForm();
    setSuccess('');
    setError('');
    setFormOpen(true);
  }

  function editarDespesa(
    despesa
  ) {
    setEditingId(
      despesa.id
    );

    setForm({
      expense_date:
        String(
          despesa.expense_date ||
          ''
        ).slice(0, 10),

      amount:
        String(
          despesa.amount ||
          ''
        ),

      category:
        despesa.category ||
        '',

      description:
        despesa.description ||
        '',

      notes:
        despesa.notes ||
        ''
    });

    setSuccess('');
    setError('');
    setFormOpen(true);
  }

  function fecharForm() {
    setFormOpen(false);
    limparForm();
  }

  async function salvarDespesa(
    event
  ) {
    event.preventDefault();

    if (!isMaster) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        expense_date:
          form.expense_date,

        amount:
          form.amount,

        category:
          form.category,

        description:
          form.description,

        notes:
          form.notes
      };

      if (editingId) {
        await apiRequest(
          `/financial/expenses/${editingId}`,
          {
            method: 'PUT',
            body:
              JSON.stringify(
                payload
              )
          }
        );

        setSuccess(
          'Despesa atualizada com sucesso.'
        );
      } else {
        await apiRequest(
          '/financial/expenses',
          {
            method: 'POST',
            body:
              JSON.stringify(
                payload
              )
          }
        );

        setSuccess(
          'Despesa cadastrada com sucesso.'
        );
      }

      fecharForm();

      await carregar();
    } catch (erro) {
      setError(
        erro.message
      );
    } finally {
      setSaving(false);
    }
  }

  async function excluirDespesa(
    despesa
  ) {
    if (!isMaster) {
      return;
    }

    const confirmar =
      window.confirm(
        `Excluir a despesa "${despesa.description}"?`
      );

    if (!confirmar) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await apiRequest(
        `/financial/expenses/${despesa.id}`,
        {
          method: 'DELETE'
        }
      );

      setSuccess(
        'Despesa excluída com sucesso.'
      );

      await carregar();
    } catch (erro) {
      setError(
        erro.message
      );
    }
  }

  const entries =
    report.entries || [];

  const expenses =
    report.expenses || [];

  return (
    <div className="space-y-6">
      {/* FINANCEIRO_RATING_FRONT_V1 */}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className={
            `text-2xl font-bold ${
              darkMode
                ? 'text-white'
                : 'text-gray-900'
            }`
          }>
            Financeiro
          </h1>

          <p className={
            `mt-1 text-sm ${subtleClass}`
          }>
            Entradas automáticas dos pagamentos aprovados e controle manual de despesas.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className={
            `flex items-center gap-2 border rounded-lg px-3 py-2 ${cardClass}`
          }>
            <CalendarDays size={18} />

            <select
              value={month}
              onChange={
                event =>
                  setMonth(
                    Number(
                      event.target.value
                    )
                  )
              }
              className={
                `bg-transparent outline-none text-sm ${
                  darkMode
                    ? 'text-white'
                    : 'text-gray-800'
                }`
              }
            >
              {MESES.map(
                (
                  nome,
                  index
                ) => (
                  <option
                    key={
                      index + 1
                    }
                    value={
                      index + 1
                    }
                    className="text-gray-900"
                  >
                    {nome}
                  </option>
                )
              )}
            </select>

            <select
              value={year}
              onChange={
                event =>
                  setYear(
                    Number(
                      event.target.value
                    )
                  )
              }
              className={
                `bg-transparent outline-none text-sm ${
                  darkMode
                    ? 'text-white'
                    : 'text-gray-800'
                }`
              }
            >
              {anos.map(
                item => (
                  <option
                    key={item}
                    value={item}
                    className="text-gray-900"
                  >
                    {item}
                  </option>
                )
              )}
            </select>
          </div>

          <button
            type="button"
            onClick={carregar}
            disabled={loading}
            className={
              `flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${cardClass}`
            }
          >
            <RefreshCw
              size={17}
              className={
                loading
                  ? 'animate-spin'
                  : ''
              }
            />
            Atualizar
          </button>

          {isMaster && (
            <button
              type="button"
              onClick={
                abrirNovaDespesa
              }
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
            >
              <Plus size={18} />
              Nova despesa
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-green-300 bg-green-50 text-green-700 px-4 py-3 text-sm">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className={
          `border rounded-xl p-5 ${cardClass}`
        }>
          <div className="flex items-center justify-between">
            <div>
              <p className={
                `text-sm ${subtleClass}`
              }>
                Entradas
              </p>

              <p className="text-2xl font-bold mt-2">
                {dinheiro(
                  summary?.entradas
                )}
              </p>
            </div>

            <div className="p-3 rounded-full bg-green-100 text-green-700">
              <WalletCards size={24} />
            </div>
          </div>

          <p className={
            `text-xs mt-3 ${subtleClass}`
          }>
            {summary?.transacoes || 0} pagamento(s) aprovado(s)
          </p>
        </div>

        <div className={
          `border rounded-xl p-5 ${cardClass}`
        }>
          <div className="flex items-center justify-between">
            <div>
              <p className={
                `text-sm ${subtleClass}`
              }>
                Despesas
              </p>

              <p className="text-2xl font-bold mt-2">
                {dinheiro(
                  summary?.despesas
                )}
              </p>
            </div>

            <div className="p-3 rounded-full bg-red-100 text-red-700">
              <ArrowDownCircle size={24} />
            </div>
          </div>

          <p className={
            `text-xs mt-3 ${subtleClass}`
          }>
            {summary?.quantidade_despesas || 0} despesa(s) no período
          </p>
        </div>

        <div className={
          `border rounded-xl p-5 ${cardClass}`
        }>
          <div className="flex items-center justify-between">
            <div>
              <p className={
                `text-sm ${subtleClass}`
              }>
                Resultado líquido
              </p>

              <p className={
                `text-2xl font-bold mt-2 ${
                  Number(
                    summary?.resultado ||
                    0
                  ) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`
              }>
                {dinheiro(
                  summary?.resultado
                )}
              </p>
            </div>

            <div className="p-3 rounded-full bg-blue-100 text-blue-700">
              <TrendingUp size={24} />
            </div>
          </div>

          <p className={
            `text-xs mt-3 ${subtleClass}`
          }>
            Entradas menos despesas
          </p>
        </div>

        <div className={
          `border rounded-xl p-5 ${cardClass}`
        }>
          <div className="flex items-center justify-between">
            <div>
              <p className={
                `text-sm ${subtleClass}`
              }>
                Ticket médio
              </p>

              <p className="text-2xl font-bold mt-2">
                {dinheiro(
                  summary?.ticket_medio
                )}
              </p>
            </div>

            <div className="p-3 rounded-full bg-purple-100 text-purple-700">
              <Receipt size={24} />
            </div>
          </div>

          <p className={
            `text-xs mt-3 ${subtleClass}`
          }>
            Média por pagamento aprovado
          </p>
        </div>
      </div>

      {formOpen && isMaster && (
        <div className={
          `border rounded-xl p-5 ${cardClass}`
        }>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold">
                {editingId
                  ? 'Editar despesa'
                  : 'Nova despesa'}
              </h2>

              <p className={
                `text-sm mt-1 ${subtleClass}`
              }>
                Informe os dados da despesa.
              </p>
            </div>

            <button
              type="button"
              onClick={fecharForm}
              className={
                `p-2 rounded-lg ${
                  darkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`
              }
            >
              <X size={20} />
            </button>
          </div>

          <form
            onSubmit={
              salvarDespesa
            }
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
          >
            <div>
              <label className="block text-sm font-medium mb-1">
                Data
              </label>

              <input
                type="date"
                required
                value={
                  form.expense_date
                }
                onChange={
                  event =>
                    setForm(
                      atual => ({
                        ...atual,
                        expense_date:
                          event.target.value
                      })
                    )
                }
                className={
                  `w-full border rounded-lg px-3 py-2 ${inputClass}`
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Valor
              </label>

              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={
                  form.amount
                }
                onChange={
                  event =>
                    setForm(
                      atual => ({
                        ...atual,
                        amount:
                          event.target.value
                      })
                    )
                }
                placeholder="0,00"
                className={
                  `w-full border rounded-lg px-3 py-2 ${inputClass}`
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Categoria
              </label>

              <input
                type="text"
                required
                maxLength={80}
                value={
                  form.category
                }
                onChange={
                  event =>
                    setForm(
                      atual => ({
                        ...atual,
                        category:
                          event.target.value
                      })
                    )
                }
                list="categorias-financeiro"
                placeholder="Ex.: Marketing"
                className={
                  `w-full border rounded-lg px-3 py-2 ${inputClass}`
                }
              />

              <datalist id="categorias-financeiro">
                <option value="Marketing" />
                <option value="Anúncios" />
                <option value="Sistemas" />
                <option value="Fornecedores" />
                <option value="Impostos" />
                <option value="Taxas" />
                <option value="Administrativo" />
                <option value="Outros" />
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Descrição
              </label>

              <input
                type="text"
                required
                maxLength={255}
                value={
                  form.description
                }
                onChange={
                  event =>
                    setForm(
                      atual => ({
                        ...atual,
                        description:
                          event.target.value
                      })
                    )
                }
                placeholder="Descrição da despesa"
                className={
                  `w-full border rounded-lg px-3 py-2 ${inputClass}`
                }
              />
            </div>

            <div className="md:col-span-2 xl:col-span-4">
              <label className="block text-sm font-medium mb-1">
                Observações
              </label>

              <textarea
                rows={3}
                value={
                  form.notes
                }
                onChange={
                  event =>
                    setForm(
                      atual => ({
                        ...atual,
                        notes:
                          event.target.value
                      })
                    )
                }
                placeholder="Observações opcionais"
                className={
                  `w-full border rounded-lg px-3 py-2 ${inputClass}`
                }
              />
            </div>

            <div className="md:col-span-2 xl:col-span-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={fecharForm}
                className={
                  `px-4 py-2 rounded-lg border text-sm font-medium ${cardClass}`
                }
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium"
              >
                <Save size={17} />

                {saving
                  ? 'Salvando...'
                  : 'Salvar despesa'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={
        `border rounded-xl overflow-hidden ${cardClass}`
      }>
        <div className="px-5 py-4 border-b border-gray-200/20">
          <h2 className="text-lg font-semibold">
            Entradas detalhadas
          </h2>

          <p className={
            `text-sm mt-1 ${subtleClass}`
          }>
            {MESES[month - 1]} de {year}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={
              darkMode
                ? 'bg-gray-900/60 text-gray-300'
                : 'bg-gray-50 text-gray-600'
            }>
              <tr>
                <th className="text-left px-4 py-3">
                  Data
                </th>
                <th className="text-left px-4 py-3">
                  Cliente
                </th>
                <th className="text-left px-4 py-3">
                  CPF/CNPJ
                </th>
                <th className="text-left px-4 py-3">
                  Forma
                </th>
                <th className="text-right px-4 py-3">
                  Valor
                </th>
              </tr>
            </thead>

            <tbody>
              {!loading &&
              entries.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className={
                      `px-4 py-8 text-center ${subtleClass}`
                    }
                  >
                    Nenhuma entrada aprovada no período.
                  </td>
                </tr>
              ) : (
                entries.map(
                  entrada => (
                    <tr
                      key={entrada.id}
                      className="border-t border-gray-200/10"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        {dataBr(
                          entrada.payment_date
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <div className="font-medium">
                          {entrada.client_name ||
                           'Cliente não identificado'}
                        </div>

                        {entrada.phone && (
                          <div className={
                            `text-xs mt-1 ${subtleClass}`
                          }>
                            {entrada.phone}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {entrada.cpf_cnpj ||
                         '-'}
                      </td>

                      <td className="px-4 py-3">
                        {entrada.payment_method ||
                         '-'}
                      </td>

                      <td className="px-4 py-3 text-right font-semibold">
                        {dinheiro(
                          entrada.amount
                        )}
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={
        `border rounded-xl overflow-hidden ${cardClass}`
      }>
        <div className="px-5 py-4 border-b border-gray-200/20 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">
              Despesas detalhadas
            </h2>

            <p className={
              `text-sm mt-1 ${subtleClass}`
            }>
              {MESES[month - 1]} de {year}
            </p>
          </div>

          {!isMaster && (
            <span className={
              `text-xs ${subtleClass}`
            }>
              Visualização somente
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={
              darkMode
                ? 'bg-gray-900/60 text-gray-300'
                : 'bg-gray-50 text-gray-600'
            }>
              <tr>
                <th className="text-left px-4 py-3">
                  Data
                </th>
                <th className="text-left px-4 py-3">
                  Categoria
                </th>
                <th className="text-left px-4 py-3">
                  Descrição
                </th>
                <th className="text-left px-4 py-3">
                  Observações
                </th>
                <th className="text-right px-4 py-3">
                  Valor
                </th>

                {isMaster && (
                  <th className="text-right px-4 py-3">
                    Ações
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {!loading &&
              expenses.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      isMaster
                        ? 6
                        : 5
                    }
                    className={
                      `px-4 py-8 text-center ${subtleClass}`
                    }
                  >
                    Nenhuma despesa cadastrada no período.
                  </td>
                </tr>
              ) : (
                expenses.map(
                  despesa => (
                    <tr
                      key={despesa.id}
                      className="border-t border-gray-200/10"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        {dataBr(
                          despesa.expense_date
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {despesa.category}
                      </td>

                      <td className="px-4 py-3">
                        {despesa.description}
                      </td>

                      <td className="px-4 py-3">
                        {despesa.notes ||
                         '-'}
                      </td>

                      <td className="px-4 py-3 text-right font-semibold">
                        {dinheiro(
                          despesa.amount
                        )}
                      </td>

                      {isMaster && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={
                                () =>
                                  editarDespesa(
                                    despesa
                                  )
                              }
                              title="Editar"
                              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                            >
                              <Pencil size={17} />
                            </button>

                            <button
                              type="button"
                              onClick={
                                () =>
                                  excluirDespesa(
                                    despesa
                                  )
                              }
                              title="Excluir"
                              className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={17} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={
        `border rounded-xl overflow-hidden ${cardClass}`
      }>
        <div className="px-5 py-4 border-b border-gray-200/20">
          <h2 className="text-lg font-semibold">
            Visão anual — {year}
          </h2>

          <p className={
            `text-sm mt-1 ${subtleClass}`
          }>
            Entradas, despesas e resultado líquido mês a mês.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={
              darkMode
                ? 'bg-gray-900/60 text-gray-300'
                : 'bg-gray-50 text-gray-600'
            }>
              <tr>
                <th className="text-left px-4 py-3">
                  Mês
                </th>
                <th className="text-right px-4 py-3">
                  Entradas
                </th>
                <th className="text-right px-4 py-3">
                  Despesas
                </th>
                <th className="text-right px-4 py-3">
                  Resultado
                </th>
                <th className="text-right px-4 py-3">
                  Transações
                </th>
                <th className="text-right px-4 py-3">
                  Ticket médio
                </th>
              </tr>
            </thead>

            <tbody>
              {(annual.months || [])
                .map(
                  item => (
                    <tr
                      key={item.month}
                      className="border-t border-gray-200/10"
                    >
                      <td className="px-4 py-3 font-medium">
                        {MESES[
                          Number(
                            item.month
                          ) - 1
                        ] || item.month}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {dinheiro(
                          item.entradas
                        )}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {dinheiro(
                          item.despesas
                        )}
                      </td>

                      <td className={
                        `px-4 py-3 text-right font-semibold ${
                          Number(
                            item.resultado ||
                            0
                          ) >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`
                      }>
                        {dinheiro(
                          item.resultado
                        )}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {item.transacoes || 0}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {dinheiro(
                          item.ticket_medio
                        )}
                      </td>
                    </tr>
                  )
                )}

              {annual.totals && (
                <tr className={
                  darkMode
                    ? 'bg-gray-900/60 font-bold'
                    : 'bg-gray-50 font-bold'
                }>
                  <td className="px-4 py-3">
                    Total do ano
                  </td>

                  <td className="px-4 py-3 text-right">
                    {dinheiro(
                      annual.totals.entradas
                    )}
                  </td>

                  <td className="px-4 py-3 text-right">
                    {dinheiro(
                      annual.totals.despesas
                    )}
                  </td>

                  <td className={
                    `px-4 py-3 text-right ${
                      Number(
                        annual.totals.resultado ||
                        0
                      ) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`
                  }>
                    {dinheiro(
                      annual.totals.resultado
                    )}
                  </td>

                  <td className="px-4 py-3 text-right">
                    {annual.totals.transacoes || 0}
                  </td>

                  <td className="px-4 py-3 text-right">
                    {dinheiro(
                      annual.totals.ticket_medio
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
